import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "askpuntt-ui-prefs";

/**
 * AskPuntt UI preference blob persisted in localStorage. Single key so we
 * don't sprinkle dozens of `askpuntt-*-seen` keys across the route. Each
 * field is optional and defaults to false on the read path.
 */
export type UiPrefs = {
  /** Opt-in native notifications when background chats become unread. */
  askPunttDesktopNotifications?: boolean;
  /** Set true after the resize-hint tooltip has been shown once. */
  resizeHintSeen?: boolean;
  /** Artifact sidebar section expand/collapse preferences. */
  artifactRail?: {
    reviewsOpen?: boolean;
    documentsOpen?: boolean;
    detailsOpen?: boolean;
  };
  /**
   * Standard/Deep artwork review-engine picker. Persisted so the choice survives
   * reloads; the composer dropdown and the artwork card toggle share this one
   * value and send it as `reviewMode` to drive per-review v1/v2 routing
   * (Standard -> v1, Deep -> v2).
   */
  artworkReviewMode?: "standard" | "deep";
};

function safeRead(): UiPrefs {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as UiPrefs;
    }
    return {};
  } catch {
    return {};
  }
}

function safeWrite(prefs: UiPrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Quota / disabled — drop silently. In-session state still holds.
  }
}

export type UseUiPrefsResult = {
  prefs: UiPrefs;
  setPref: <K extends keyof UiPrefs>(key: K, value: UiPrefs[K]) => void;
};

/**
 * Read + write the single askpuntt-ui-prefs JSON blob in localStorage.
 *
 * Designed so adding a new one-shot UI flag (e.g., "new-feature-coachmark
 * seen") doesn't add another storage key — just another field on
 * `UiPrefs`. Reads are synchronous-on-mount; writes flush immediately
 * (no debounce — these are rare, terminal events).
 */
export function useUiPrefs(): UseUiPrefsResult {
  const [prefs, setPrefs] = useState<UiPrefs>(() => safeRead());

  // If another tab updates the blob, mirror it locally so the hint
  // doesn't pop up again after being seen in a sibling tab.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setPrefs(safeRead());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setPref = useCallback(
    <K extends keyof UiPrefs>(key: K, value: UiPrefs[K]) => {
      setPrefs((prev) => {
        const next = { ...prev, [key]: value };
        safeWrite(next);
        return next;
      });
    },
    [],
  );

  return { prefs, setPref };
}
